import { unwrapPayloadEnvelope, isHollowPayload } from "@/utils/extractPayloadFields"
import { isMasterSyncConfig } from "@/utils/endpointLabel"
import {
  FALLBACK_DIMENSIONS,
  type DimensionProperty,
  type ResolvedDimension,
} from "@/pages/DataDimensionStudio/catalog"

export const MASTER_DATA_SUPPORT_PACKAGE = "Master Data Sync"

export const MASTER_DATA_DESTINATION = {
  type: "Master Data",
  name: "Master Data",
} as const

/** Fields to stamp on a new Master Data Sync integration. Destination is internal. */
export const masterDataSyncCreateFields = () => ({
  supportPackage: MASTER_DATA_SUPPORT_PACKAGE,
  destination: { type: "Master Data", name: "Master Data" },
  destinationConnectorType: "Master Data",
  integrationMode: "pipeline" as const,
})

export const isMasterDataSync = (connector?: any, config?: any): boolean =>
  isMasterSyncConfig(connector, config ?? connector)

const SYSTEM_FIELD_KEYS = new Set([
  "recordId",
  "createdOn",
  "lastUpdatedOn",
  "createdBy",
  "lastUpdatedBy",
])

export const isMappableMasterProperty = (prop: DimensionProperty): boolean =>
  !!prop?.isActive && !prop.isSystemField && !prop.readOnly && !SYSTEM_FIELD_KEYS.has(prop.key)

export const getMasterSyncDimensionKey = (connector?: any): string | undefined => {
  const key =
    connector?.masterSync?.dimensionKey ||
    connector?.masterSync?.resource ||
    connector?.destination?.dimensionKey
  if (!key || typeof key !== "string") return undefined
  const normalized = key.trim().toLowerCase()
  if (normalized === "vehicles") return "vehicle"
  if (normalized === "drivers") return "driver"
  return normalized
}

export const findMasterDimension = (
  dimensionKey: string | undefined,
  dimensions: ResolvedDimension[] = FALLBACK_DIMENSIONS
): ResolvedDimension | undefined => {
  if (!dimensionKey) return undefined
  return dimensions.find(d => d.dimensionKey === dimensionKey)
}

export const mappableMasterProperties = (dimension?: ResolvedDimension): DimensionProperty[] =>
  (dimension?.properties || []).filter(isMappableMasterProperty)

export const requiredMasterProperties = (dimension?: ResolvedDimension): DimensionProperty[] =>
  mappableMasterProperties(dimension).filter(p => p.required)

export const identityMasterProperty = (dimension?: ResolvedDimension): DimensionProperty | undefined => {
  const mappable = mappableMasterProperties(dimension)
  return mappable.find(p => p.unique && p.required) || mappable.find(p => p.required)
}

const sampleValueForType = (prop: DimensionProperty): unknown => {
  switch ((prop.dataType || "string").toLowerCase()) {
    case "number":
      return 0
    case "boolean":
      return false
    case "array":
      return []
    case "object":
      return {}
    case "enum":
      return prop.enumValues?.[0] || ""
    default:
      return ""
  }
}

export const buildMasterDestinationFromDimension = (dimension: ResolvedDimension) => {
  const properties = mappableMasterProperties(dimension)
  const variables: Record<string, unknown> = {}
  const customFieldsMetadata: Record<string, any> = {}

  properties.forEach(prop => {
    variables[prop.key] = sampleValueForType(prop)
    customFieldsMetadata[prop.key] = {
      label: prop.label,
      dataType: prop.dataType,
      required: !!prop.required,
      unique: !!prop.unique,
      description: prop.description,
      enumValues: prop.enumValues,
      isCustom: !!prop.isCustom,
      origin: prop.origin,
    }
  })

  return {
    type: "Master Data",
    name: dimension.name,
    dimensionKey: dimension.dimensionKey,
    variables,
    customFieldsMetadata,
  }
}

export const masterSyncStamp = (dimension: ResolvedDimension) => ({
  masterSync: {
    enabled: true,
    dimensionKey: dimension.dimensionKey,
    resource: dimension.dimensionKey,
  },
  destination: buildMasterDestinationFromDimension(dimension),
  destinationConnectorType: "Master Data",
  supportPackage: MASTER_DATA_SUPPORT_PACKAGE,
})

const normalizeField = (value: string): string =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")

export interface SchemaFitResult {
  requiredTotal: number
  requiredMatched: number
  optionalMatched: number
  optionalTotal: number
  suggestions: Array<{ source: string; target: string; required: boolean }>
}

export const scoreMasterSchemaFit = (
  sourceFields: string[],
  dimension?: ResolvedDimension
): SchemaFitResult => {
  const properties = mappableMasterProperties(dimension)
  const required = properties.filter(p => p.required)
  const optional = properties.filter(p => !p.required)
  const suggestions: SchemaFitResult["suggestions"] = []

  const matchProp = (prop: DimensionProperty): string | undefined => {
    const targetNorm = normalizeField(prop.key)
    const labelNorm = normalizeField(prop.label)
    return sourceFields.find(src => {
      const srcNorm = normalizeField(src)
      if (!srcNorm || !targetNorm) return false
      return (
        srcNorm === targetNorm ||
        srcNorm === labelNorm ||
        srcNorm.endsWith(targetNorm) ||
        targetNorm.endsWith(srcNorm) ||
        (targetNorm.length >= 4 && srcNorm.includes(targetNorm)) ||
        (srcNorm.length >= 4 && targetNorm.includes(srcNorm))
      )
    })
  }

  required.forEach(prop => {
    const source = matchProp(prop)
    if (source) suggestions.push({ source, target: prop.key, required: true })
  })
  optional.forEach(prop => {
    const source = matchProp(prop)
    if (source) suggestions.push({ source, target: prop.key, required: false })
  })

  return {
    requiredTotal: required.length,
    requiredMatched: suggestions.filter(s => s.required).length,
    optionalMatched: suggestions.filter(s => !s.required).length,
    optionalTotal: optional.length,
    suggestions,
  }
}

export const unmappedRequiredMasterFields = (
  edges: Array<{ targetPath?: string; targetHandle?: string }> | undefined,
  dimension?: ResolvedDimension
): DimensionProperty[] => {
  const mapped = new Set<string>()
  ;(Array.isArray(edges) ? edges : []).forEach(edge => {
    const raw = String(edge.targetPath || edge.targetHandle || "")
    const cleaned = raw.replace(/-target$/i, "")
    if (cleaned) {
      mapped.add(cleaned)
      mapped.add(cleaned.split(".").pop() || cleaned)
      mapped.add(cleaned.split("_").pop() || cleaned)
    }
  })
  return requiredMasterProperties(dimension).filter(prop => !mapped.has(prop.key))
}

export const sourcePayloadObject = (source: any): Record<string, unknown> | undefined => {
  if (!source || typeof source !== "object") return undefined
  const vars = source.variables
  const fromVars =
    vars && typeof vars === "object" && !Array.isArray(vars) && Object.keys(vars).length > 0
      ? vars
      : undefined
  const raw = source.body?.rawContent
  const rawText =
    typeof raw === "string"
      ? raw
      : raw && typeof raw === "object"
        ? raw.content || raw.rawContent
        : undefined
  let fromBody: Record<string, unknown> | undefined
  if (typeof rawText === "string" && rawText.trim()) {
    try {
      const parsed = JSON.parse(rawText)
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
        fromBody = parsed
      }
    } catch {
      /* ignore */
    }
  }
  const asSchemaObject = (value: any): Record<string, unknown> | undefined => {
    if (!value) return undefined
    const unwrapped = unwrapPayloadEnvelope(value)
    if (!isHollowPayload(unwrapped) && unwrapped && typeof unwrapped === "object" && !Array.isArray(unwrapped)) {
      return unwrapped as Record<string, unknown>
    }
    if (!isHollowPayload(value) && typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, unknown>
    }
    return undefined
  }

  return (
    asSchemaObject(fromVars) ||
    asSchemaObject(fromBody) ||
    asSchemaObject(source.schemaSample) ||
    asSchemaObject(source.samplePayload)
  )
}

export const flattenSourceFieldNames = (source: any): string[] => {
  const names = new Set<string>()
  const walk = (value: any, prefix = "") => {
    if (!value || typeof value !== "object") return
    if (Array.isArray(value)) {
      if (value[0] && typeof value[0] === "object") walk(value[0], prefix)
      return
    }
    Object.keys(value).forEach(key => {
      const path = prefix ? `${prefix}.${key}` : key
      names.add(key)
      names.add(path)
      if (value[key] && typeof value[key] === "object") walk(value[key], path)
    })
  }
  walk(sourcePayloadObject(source) || source?.variables || source)
  return [...names]
}

export const pickRicherSource = (a?: any, b?: any) => {
  const score = (source: any) => {
    if (!source || typeof source !== "object") return 0
    return flattenSourceFieldNames(source).length * 10 + Object.keys(source).length
  }
  return score(a) >= score(b) ? a || b : b || a
}

/** API GET returns `{ connector, config }` — prefer the copy that still has the validated payload. */
export const flattenConnectorResponse = (raw: any) => {
  if (!raw || typeof raw !== "object") return raw
  const connector = raw.connector || raw
  const config = raw.config || {}
  return {
    ...connector,
    source: pickRicherSource(config.source, connector?.source),
    destination: config.destination?.dimensionKey || config.destination?.variables
      ? config.destination
      : connector?.destination || config.destination,
    dataMapping: config.dataMapping ?? connector?.dataMapping,
    enrichment: config.enrichment ?? connector?.enrichment,
    additionalDestinations:
      config.additionalDestinations ??
      connector?.additionalDestinations ??
      raw.additionalDestinations,
    cacheOperations: config.cacheOperations ?? connector?.cacheOperations,
    masterSync: connector?.masterSync ?? config.masterSync,
    customProcessor: config.customProcessor ?? connector?.customProcessor,
    changeDetection: connector?.changeDetection ?? config.changeDetection,
  }
}
